import { useCallback, useEffect, useRef, useState } from 'react';

import {
    loadPrinterSelection,
    selectPrinter,
    type PrinterSelectionState,
} from '../domain/labelPrinting';
import {
    buildPrinterSelectionView,
    isNewerServiceState,
    type PrinterSelectionView,
} from '../domain/printerSelection';

/**
 * The one piece of printer-selection state, shared by Listings and Configure → Printers.
 *
 * Both screens are selectors over the same local hardware-service preference, so both use this.
 * Duplicating the fetch-and-reconcile logic in two components is how they would come to disagree
 * about which printer is effective — and the operator would have no way to tell which was right.
 *
 * All the rules live in `domain/printerSelection`; this holds only the React plumbing.
 */
export interface UsePrinterSelection {
    view: PrinterSelectionView;
    /** Raw service state, for callers that need the printer id to confirm on a print. */
    state: PrinterSelectionState | null;
    loading: boolean;
    busy: boolean;
    /**
     * Re-reads the service. `refresh` forces a platform enumeration rather than its cache.
     *
     * Returns the state it accepted, because a caller about to print needs the answer *now* and
     * cannot wait for a React render to observe it.
     */
    reload: (refresh?: boolean) => Promise<PrinterSelectionState | null>;
    /** Chooses a printer, or clears the choice with null. */
    choose: (printerId: string | null) => Promise<boolean>;
}

/** How often the panel reconciles while it is on screen. */
const RECONCILE_INTERVAL_MS = 20_000;

export function usePrinterSelection(options: { enabled: boolean }): UsePrinterSelection {
    const { enabled } = options;

    const [state, setState] = useState<PrinterSelectionState | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    // Held in a ref as well as state so the ordering check reads the value that is current *now*,
    // not the one captured when the callback was created.
    const latest = useRef<PrinterSelectionState | null>(null);

    const accept = useCallback((incoming: PrinterSelectionState | null) => {
        if (incoming === null) {
            return null;
        }

        // Several tabs, a reconcile tick and a manual refresh can land together. The service
        // stamps `observedAt` from its own clock, so the newest platform read wins rather than
        // whichever response happened to resolve last.
        if (isNewerServiceState(incoming, latest.current)) {
            latest.current = incoming;
            setState(incoming);

            return incoming;
        }

        return latest.current;
    }, []);

    const reload = useCallback(
        async (refresh = false) => {
            setLoading(true);
            try {
                return accept(await loadPrinterSelection(window.fetch.bind(window), { refresh }));
            } finally {
                setLoading(false);
            }
        },
        [accept],
    );

    const choose = useCallback(async (printerId: string | null) => {
        setBusy(true);
        try {
            const updated = await selectPrinter(window.fetch.bind(window), printerId);

            if (updated === null) {
                return false;
            }

            // A confirmed selection is authoritative regardless of timestamps: it is the
            // service telling us what it just decided, not an observation racing another.
            latest.current = updated;
            setState(updated);

            return true;
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        void reload();

        // Bounded reconciliation, not a poll loop: the service already re-reads the platform
        // before every print, so this exists only so a screen left open reflects a printer being
        // plugged in without the operator reaching for refresh.
        const timer = window.setInterval(() => void reload(true), RECONCILE_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [enabled, reload]);

    return {
        view: buildPrinterSelectionView(state),
        state,
        loading,
        busy,
        reload,
        choose,
    };
}
